import { DateTime } from 'luxon';
import { z } from 'zod';

import { SubLocationsOfLocationListSchema } from '@/lib/api/schemas/sublocations.ts';
import { parseUnixTimestamp } from '@/utils/dates';
import { zodPlate, zodPlateState, zodTimeStamp } from '@/utils/zodUtils';

import { createPaginatedResultSchema } from './shared/paginated';
import { Vehicle, VehicleWithRequiredPlateFields } from './vehicle';

const PermitSubscriptionPlan = z
  .object({
    id: z.string(),
    amount: z.number(),
    created: z.number().transform((val) => parseUnixTimestamp(val)),
    currency: z.string(),
    interval: z.string(),
    active: z.boolean(),
    trial_period_days: z.number(),
  })
  .passthrough();

const PermitSubscriptionItem = z
  .object({
    id: z.string(),
    created: z.number().transform((val) => parseUnixTimestamp(val)),
    plan: PermitSubscriptionPlan,
  })
  .passthrough();

const PermitSubscription = z
  .object({
    id: z.string(),
    currency: z.string(),
    status: z.enum([
      'active',
      'past_due',
      'canceled',
      'paused',
      'unpaid',
      'incomplete_expired',
    ]),
    items: z
      .object({
        total_count: z.number(),
        data: z.array(PermitSubscriptionItem),
      })
      .passthrough(),
    cancel_at_period_end: z.boolean(),
    created: z.number().transform((val) => parseUnixTimestamp(val)),
    start_date: z.number().transform((val) => parseUnixTimestamp(val)),
    // Stripe-derived timestamps below are absent in some subscription
    // states (e.g. trial-only, never-cancelled, current_period_* moved to
    // SubscriptionItem in newer API versions). Allow null/undefined.
    ended_at: z
      .number()
      .nullish()
      .transform((val) => (val == null ? null : parseUnixTimestamp(val))),
    current_period_start: z
      .number()
      .nullish()
      .transform((val) => (val == null ? null : parseUnixTimestamp(val))),
    current_period_end: z
      .number()
      .nullish()
      .transform((val) => (val == null ? null : parseUnixTimestamp(val))),
    cancel_at: z
      .number()
      .nullish()
      .transform((val) => (val == null ? null : parseUnixTimestamp(val))),
    canceled_at: z
      .number()
      .nullish()
      .transform((val) => (val == null ? null : parseUnixTimestamp(val))),
    trial_start: z
      .number()
      .nullish()
      .transform((val) => (val == null ? null : parseUnixTimestamp(val))),
    trial_end: z
      .number()
      .nullish()
      .transform((val) => (val == null ? null : parseUnixTimestamp(val))),
  })
  .passthrough();

export const PermitCustomer = z
  .object({
    id: z.string(),
    email: z
      .string()
      .optional()
      .refine(
        (email) => !email || z.string().email().safeParse(email).success,
        {
          message: 'Invalid email format',
        }
      ),
    name: z.string().optional(),
    phone: z.string().optional(),
    created: z.number().transform((val) => parseUnixTimestamp(val)),
    invoice_prefix: z.string().optional(),
  })
  .passthrough();

const PermitInvoice = z
  .object({
    id: z.string(),
    number: z.string(),
    account_name: z.string(),
    total: z.number(),
    currency: z.string(),
    status: z.enum([
      'paid',
      'failed',
      'overdue',
      'draft',
      'open',
      'void',
      'uncollectible',
    ]),
    billing_reason: z.string(),
    collection_method: z.string(),
    customer_name: z.string(),
    customer_email: z.string(),
    created: z.number().transform((val) => parseUnixTimestamp(val)),
    effective_at: z.number().transform((val) => parseUnixTimestamp(val)),
    invoice_pdf: z.string().nullable().optional(),
    charge: z
      .object({ receipt_url: z.string().optional() })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough();

const Billing = z.object({
  subscription: PermitSubscription.nullable().optional(),
  customer: PermitCustomer.nullable().optional(),
  invoices: z.array(PermitInvoice).nullable().optional(),
});

export enum PermitStatus {
  Active = 'active',
  PastDue = 'past_due',
  Canceled = 'canceled',
  Paused = 'paused',
  Unpaid = 'unpaid',
  NeedsApproval = 'needs_approval',
  Approved = 'approved',
  Expired = 'expired',
}

const PermitSchedulePeriod = z.object({
  days_of_week: z.array(z.string()),
  start_time_secs: z.number(),
  end_time_secs: z.number(),
  hours_type: z.string(),
});

const PermitSchedule = z.object({
  always_open: z.boolean(),
  periods: z.array(PermitSchedulePeriod).default([]),
});

export const PermitCoverageKindSchema = z.enum([
  'main_lot',
  'sub_lot',
  'location_group',
]);

// Trailer / second-vehicle plates stored API-side on permits.additional_plates.
// Shape mirrors the API CarPlate ({ number, state }), which differs from the
// primary `vehicle` shape ({ plate, plateState }). Kept permissive (plain
// strings) because API responses are parsed with sync `.parse()`.
export const PermitAdditionalPlate = z.object({
  number: z.string(),
  state: z.string(),
  // Levenshtein edit distance between the search plate and this additional
  // plate. Only present on fuzzy permit search results; 0 means an exact match.
  plateLevenstianDistance: z.number().optional(),
});

export const Permit = z.object({
  permit_id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  phone: z.string(),
  company: z.string(),
  location_id: z.string(),
  permit_coverage: PermitCoverageKindSchema.optional(),
  location_group_key: z.string().optional(),
  location_group_member_ids: z.array(z.string()).optional(),
  vehicle: Vehicle,
  additional_plates: z.array(PermitAdditionalPlate).optional(),
  permit_type: z.string(),
  status: z.nativeEnum(PermitStatus).optional(),
  created_at: zodTimeStamp.nullable(),
  updated_at: zodTimeStamp.nullish(),
  expires_at: zodTimeStamp.nullish(),
  next_renewal_at: zodTimeStamp.nullish(),
  starts_at: zodTimeStamp.nullish(),

  // Levenshtein edit distance between the search plate and the permit's plate.
  // Only populated when fuzzy search is enabled. 0 means exact match.
  plateLevenstianDistance: z.number().optional(),

  // Billing Details if it's a subscription
  billing: Billing.nullable().optional(),

  // Schedule
  schedule: PermitSchedule.nullable().optional(),

  // Address verification details
  address_verification_id: z.string().nullable().optional(),
  address_verification: z
    .object({
      status: z.string(),
      verified_at: zodTimeStamp.nullable().optional(),
      expires_at: zodTimeStamp.nullable().optional(),
      document_type: z.string().nullable().optional(),
      extracted_city: z.string().nullable().optional(),
      extracted_state: z.string().nullable().optional(),
      extracted_zip: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});

export const PermitCustomerUpdate = z.object({
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  email: z.string().optional(),
  vehicle: VehicleWithRequiredPlateFields,
});

export const PermitBillingInformationUpdate = z.object({
  email: z.string().email().optional(),
  name: z.string().optional(),
  phone: z.string().optional(),
});

export enum CancellationBehavior {
  Immediate = 'immediate',
  EndOfBillingCycle = 'end_of_billing_cycle',
}

export enum CancellationFeedback {
  CustomerService = 'customer_service',
  LowQuality = 'low_quality',
  MissingFeatures = 'missing_features',
  SwitchedService = 'switched_service',
  TooComplex = 'too_complex',
  TooExpensive = 'too_expensive',
  Unused = 'unused',
  Other = 'other',
}

export const CancelSubscriptionSchema = z.object({
  cancellationBehavior: z.nativeEnum(CancellationBehavior),
  feedback: z.nativeEnum(CancellationFeedback).optional(),
});

// Additional (trailer/second) plate caps per permit type, mirrored from the API
// (permit.MaxAdditionalPlates): temporary permits carry one, every other type
// up to three.
export const MAX_ADDITIONAL_PLATES_DEFAULT = 3;
const MAX_ADDITIONAL_PLATES_TEMPORARY = 1;

export function maxAdditionalPlatesForType(permitType: string): number {
  return permitType === 'temporary'
    ? MAX_ADDITIONAL_PLATES_TEMPORARY
    : MAX_ADDITIONAL_PLATES_DEFAULT;
}

export const UpdatePermitSchema = z.object({
  first_name: z.string().trim(),
  last_name: z
    .string()
    .trim()
    .transform((v) => v || undefined)
    .optional(),
  email: z
    .string()
    .trim()
    .email({ message: 'Invalid email address' })
    .or(z.literal('')),
  phone: z
    .string()
    .trim()
    .transform((v) => v || undefined)
    .optional(),
  company: z
    .string()
    .trim()
    .transform((v) => v || undefined)
    .optional(),
  vehicle: z.object({
    plate: z
      .string()
      .trim()
      .transform((v) => v.toUpperCase()),
    state: z.string(),
    make: z.string(),
    model: z.string(),
    color: z.string(),
  }),
  // Trailer / second plates. Sent to the API as { number, state } (the API
  // CarPlate shape), which differs from the primary `vehicle`. This is the
  // absolute ceiling; the per-permit-type cap is enforced in the card UI and
  // authoritatively by the API. `zodPlateState` is an async refinement, so this
  // schema must be resolved with `zodResolver`/`parseAsync`.
  additional_plates: z
    .array(z.object({ number: zodPlate, state: zodPlateState }))
    .max(
      MAX_ADDITIONAL_PLATES_DEFAULT,
      `At most ${MAX_ADDITIONAL_PLATES_DEFAULT} additional plates are allowed`
    )
    .optional(),
});
export type UpdatePermitSchemaType = z.infer<typeof UpdatePermitSchema>;

export const CreatePermitVehicle = z.object({
  plate: z.string().trim().min(1, { message: 'Plate is required' }),
  state: z.string().trim().min(1, { message: 'State is required' }),
  make: z.string().trim().min(1, { message: 'Make is required' }),
  model: z.string().trim().min(1, { message: 'Model is required' }),
  color: z.string().trim().min(1, { message: 'Color is required' }),
});

/** Vehicle fields for quick permit (plate + state required; make/model/color optional). */
export const QuickPermitVehicleSchema = z.object({
  plate: z
    .string()
    .trim()
    .min(1, { message: 'Plate is required' })
    .transform((v) => v.toUpperCase()),
  state: z.string().trim().min(1, { message: 'State is required' }),
  make: z.string().trim().optional().default(''),
  model: z.string().trim().optional().default(''),
  color: z.string().trim().optional().default(''),
});

/**
 * Validates temporary permit window: required fields, parseable, end > start.
 *
 * The "start must not be in the past" rule is intentionally NOT enforced here:
 * pickers render in the user's preferred timezone but `Settings.defaultZone`
 * is `'UTC'` globally, so a Zod-level comparison against `DateTime.now()` can
 * skew the answer by hours. The picker's `disablePast` (rendered in the
 * correct zone) and the server's own validation are the authoritative gates.
 */
function refineTemporaryPermitSchedule(
  data: {
    permit_type: string;
    starts_at?: string | null;
    expires_at?: string | null;
  },
  ctx: z.RefinementCtx
) {
  if (data.permit_type !== 'temporary') {
    return;
  }
  const startS = (data.starts_at ?? '').trim();
  const endS = (data.expires_at ?? '').trim();
  if (!startS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Start date is required',
      path: ['starts_at'],
    });
  }
  if (!endS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'End date is required',
      path: ['expires_at'],
    });
  }
  if (!startS || !endS) {
    return;
  }
  const start = DateTime.fromISO(startS);
  const end = DateTime.fromISO(endS);
  if (!start.isValid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Start date and time are invalid',
      path: ['starts_at'],
    });
    return;
  }
  if (!end.isValid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'End date and time are invalid',
      path: ['expires_at'],
    });
    return;
  }
  if (end <= start) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'End must be after start',
      path: ['expires_at'],
    });
  }
}

export const QuickPermitFormSchema = z
  .object({
    locationId: z.string().min(1, { message: 'Location id is required' }),
    permit_type: z.enum(['temporary', 'permanent']),
    permit_coverage: PermitCoverageKindSchema.optional(),
    location_group_key: z.string().optional(),
    location_group_member_ids: z.array(z.string().min(1)).optional(),
    /** Sublocation scope (display name), same as full create `company` when set */
    company: z.string().trim().optional(),
    first_name: z
      .string()
      .trim()
      .min(2, { message: 'First name must be at least 2 characters' }),
    last_name: z.string().trim().min(1, { message: 'Last name is required' }),
    email: z
      .string()
      .trim()
      .min(1, { message: 'Email is required' })
      .email({ message: 'Invalid email address' }),
    phone: z
      .string()
      .trim()
      .refine(
        (v) =>
          v === '' || (v.replace(/\D/g, '').length >= 10 && v.length <= 20),
        { message: 'Enter a valid phone or leave blank' }
      ),
    starts_at: z.string().optional(),
    expires_at: z.string().optional(),
    vehicle: QuickPermitVehicleSchema,
  })
  .superRefine(refineTemporaryPermitSchedule);

export type QuickPermitFormSchemaType = z.infer<typeof QuickPermitFormSchema>;

/** Multi-location create when all selected locations have quick permit enabled. */
export const CreateQuickMultiLocationPermitSchema = z
  .object({
    locations: SubLocationsOfLocationListSchema.min(1, {
      message: 'At least one location is required',
    }),
    permit_type: z.enum(['temporary', 'permanent']),
    first_name: z
      .string()
      .trim()
      .min(2, { message: 'First name must be at least 2 characters' }),
    last_name: z.string().trim().min(1, { message: 'Last name is required' }),
    phone: z
      .string()
      .trim()
      .refine(
        (v) =>
          v === '' || (v.replace(/\D/g, '').length >= 10 && v.length <= 20),
        { message: 'Enter a valid phone or leave blank' }
      ),
    email: z
      .string()
      .trim()
      .min(1, { message: 'Email is required' })
      .email({ message: 'Invalid email address' }),
    starts_at: z.string().optional(),
    expires_at: z.string().optional(),
    vehicle: QuickPermitVehicleSchema,
  })
  .superRefine(refineTemporaryPermitSchedule);

export type CreateQuickMultiLocationPermitSchemaType = z.infer<
  typeof CreateQuickMultiLocationPermitSchema
>;

export const CreatePermitSchema = z.object({
  locationId: z.string().min(1, { message: 'Location id is required' }),
  permit_coverage: PermitCoverageKindSchema.optional(),
  location_group_key: z.string().optional(),
  location_group_member_ids: z.array(z.string().min(1)).optional(),
  permit_type: z.string().min(1, { message: 'Permit type is required' }),
  first_name: z
    .string()
    .trim()
    .min(2, { message: 'First name must be at least 2 characters' }),
  last_name: z.string().trim().min(1, { message: 'Last name is required' }),
  phone: z
    .string()
    .min(10, { message: 'Phone must be at least 10 digits' })
    .max(15, { message: 'Phone must be at most 15 digits' }),
  email: z
    .string()
    .trim()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email address' }),
  company: z.string().nullish(),
  starts_at: z.string().optional(),
  expires_at: z.string().optional(),
  vehicle: CreatePermitVehicle,
});

// Schema for multi-location permit creation
export const CreateMultiLocationPermitSchema = z
  .object({
    locations: SubLocationsOfLocationListSchema.min(1, {
      message: 'At least one location is required',
    }),
    permit_type: z.string().min(1, { message: 'Permit type is required' }),
    first_name: z
      .string()
      .trim()
      .min(2, { message: 'First name must be at least 2 characters' }),
    last_name: z.string().trim().min(1, { message: 'Last name is required' }),
    phone: z
      .string()
      .min(10, { message: 'Phone must be at least 10 digits' })
      .max(15, { message: 'Phone must be at most 15 digits' }),
    email: z
      .string()
      .trim()
      .min(1, { message: 'Email is required' })
      .email({ message: 'Invalid email address' }),
    starts_at: z.string().optional(),
    expires_at: z.string().optional(),
    vehicle: CreatePermitVehicle,
  })
  .superRefine(refineTemporaryPermitSchedule);

export type PermitScheduleType = z.infer<typeof PermitSchedule>;
export type PermitSchedulePeriodType = z.infer<typeof PermitSchedulePeriod>;
export type PermitType = z.infer<typeof Permit>;
export type PermitAdditionalPlateType = z.infer<typeof PermitAdditionalPlate>;
export const PermitList = z.array(Permit);
export type PermitListType = z.infer<typeof PermitList>;

// GET /permits cursor. Mirrors permit.PermitStartKey on the Go side:
// composite (createdAtMillis, permitId) for stable keyset pagination.
export const PermitPaginationKeySchema = z.object({
  createdAtMillis: z.number(),
  permitId: z.string(),
});
export type PermitPaginationKey = z.infer<typeof PermitPaginationKeySchema>;

export const PermitsPaginatedResult = createPaginatedResultSchema(
  Permit,
  PermitPaginationKeySchema
);
export type PermitsPaginatedResultType = z.infer<typeof PermitsPaginatedResult>;

// GET /permits/coverage - point-in-time permit coverage lookup (ENG-2264).
//
// This is the ONLY permit read that can reach soft-deleted rows. The ordinary
// GET /permits list filters `deleted_at IS NULL` and answers a "now" question;
// this endpoint answers "what covered this plate at the violation instant" and
// returns permits that covered the stay but have since been removed, flagged
// `superseded`.

// The exact plate that matched (primary or one of additional_plates). `state`
// is the permit's real stored state for that plate, which can differ from the
// citation's forced state (ENG-2201); display it, do not assume it matches.
export const PermitMatchedPlate = z.object({
  number: z.string(),
  state: z.string(),
});
export type PermitMatchedPlateType = z.infer<typeof PermitMatchedPlate>;

export const PermitCoverageMatch = z.object({
  // The full permit, identical shape to a GET /permits list item.
  permit: Permit,
  // true when the permit was soft-deleted strictly after the violation instant:
  // it covered the stay but has since been removed. The load-bearing flag, since
  // this is exactly the case the "now" list cannot show.
  superseded: z.boolean(),
  // The deleted_at moment when superseded; null/absent otherwise. Always after
  // the queried instant.
  superseded_at: zodTimeStamp.nullish(),
  // true when the match was on one of the permit's additional_plates (trailer /
  // second vehicle) rather than the primary plate.
  matched_on_additional_plate: z.boolean(),
  matched_plate: PermitMatchedPlate,
  // The permit's schedule evaluated against the instant in the cited location's
  // timezone. `false` = the permit has a schedule that does NOT admit the
  // instant (a signal to scrutinize, NOT a reason it was withheld); `true` =
  // schedule admits it; null/absent = no schedule, always-open, or the timezone
  // could not be resolved. Surfaced for the human to judge, exactly like status;
  // the permit is never dropped on this.
  schedule_active_at_instant: z.boolean().nullish(),
  // Levenshtein edit distance of the plate this row matched on (0 = exact).
  // Present ONLY in fuzzy mode (`?fuzzy=true`); absent in exact mode, so an
  // exact-mode row carries no distance and its match quality is derived
  // client-side as before. A 0 here is a real exact match, never a misread.
  match_distance: z.number().int().nullish(),
  // True when the permit was created strictly AFTER the violation instant, so it
  // could not have covered the stay although its plate matches now. Fuzzy mode
  // surfaces such rows (marked) instead of dropping them; exact mode filters
  // them out and never sends the key. Optional (not `.default`) so the inferred
  // type stays back-compatible with existing exact-mode fixtures; an absent key
  // is treated as false where the row is built (`toPermitCoverageRows`).
  created_after_instant: z.boolean().optional(),
});
export type PermitCoverageMatchType = z.infer<typeof PermitCoverageMatch>;

export const PermitCoverageResult = z.object({
  // Every permit that covered the plate at the instant; most-recently-created
  // first. Empty array (never null) when nothing covered.
  permits: z.array(PermitCoverageMatch),
});
export type PermitCoverageResultType = z.infer<typeof PermitCoverageResult>;
export type PermitBillingType = z.infer<typeof Billing>;
export type PermitSubscriptionType = z.infer<typeof PermitSubscription>;
export type PermitSubscriptionPlanType = z.infer<typeof PermitSubscriptionPlan>;
export type PermitCustomerType = z.infer<typeof PermitCustomer>;
export type PermitCustomerUpdateType = z.infer<typeof PermitCustomerUpdate>;

export type PermitBillingInformationUpdateType = z.infer<
  typeof PermitBillingInformationUpdate
>;

export type PermitInvoiceType = z.infer<typeof PermitInvoice>;
export const PermitInvoiceList = z.array(PermitInvoice);
export type PermitInvoiceListType = z.infer<typeof PermitInvoiceList>;

export type CancelSubscriptionSchemaType = z.infer<
  typeof CancelSubscriptionSchema
>;

export type CreatePermitSchemaType = z.infer<typeof CreatePermitSchema>;
export type CreateMultiLocationPermitSchemaType = z.infer<
  typeof CreateMultiLocationPermitSchema
>;
